import { useCallback, useEffect, useState } from 'react';
import { domainService } from '../services/domainService';
import type { DnsZoneResponse } from '../types/dns.types';

/**
 * Loads the DNS zone for a domain via the whm-dns-list EF. Exposes a
 * refetch() callback so consumers can refresh after a mutation. Replaces
 * the prior `useDnsRecords` stub which was deleted in G1.
 */
export function useDnsZone(domain: string | undefined) {
  const [data, setData] = useState<DnsZoneResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!domain) return;
    setIsLoading(true);
    setError(null);
    try {
      const r = await domainService.listDnsRecords(domain);
      setData(r);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [domain]);

  useEffect(() => {
    if (!domain) {
      setIsLoading(false);
      return;
    }
    void fetchOnce();
  }, [domain, fetchOnce]);

  return { data, isLoading, error, refetch: fetchOnce };
}
